/**
 * The RL preset a GRPO run trains, picked by label (TRA-1491): the launch
 * dialog and the scheduled run's form both offer it. A preset outside the
 * offered ones (a continued run's, an older schedule's) is listed by its
 * name, so it stays chosen until changed.
 */

import type { TrainingGrpoPresetOption } from '@/api/generated/model'
import { FieldError } from '@/components/ui/field-error'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export function PresetField({
  id,
  presets,
  value,
  error,
  disabled = false,
  onChange,
}: {
  id: string
  presets: TrainingGrpoPresetOption[]
  value: string
  /** Why the preset cannot be used; the summary line shows otherwise. */
  error: string | undefined
  disabled?: boolean
  onChange: (name: string) => void
}): React.JSX.Element {
  const chosen = presets.find((option) => option.name === value)
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Preset</Label>
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger id={id} aria-invalid={error !== undefined} aria-describedby={`${id}-note`}>
          <SelectValue placeholder="Choose a preset" />
        </SelectTrigger>
        <SelectContent>
          {chosen === undefined && value !== '' && (
            <SelectItem value={value}>
              <span className="font-mono text-xs">{value}</span>
            </SelectItem>
          )}
          {presets.map((option) => (
            <SelectItem key={option.name} value={option.name}>
              <span className="flex items-baseline gap-2">
                <span>{option.label}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{option.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error === undefined && (
        <p id={`${id}-note`} className="text-xs text-muted-foreground">
          {chosen?.summary ?? (value === '' ? 'Choose a preset.' : 'A preset the Training page does not offer.')}
        </p>
      )}
      <FieldError id={`${id}-note`} message={error} />
    </div>
  )
}
